<!-- eslint-disable max-len -->
<template>
  <div
    class="document-portal-view"
    :class="{ 'is-complete': clientPortalStore.currentStep === 'complete'}"
  >
    <Callout
      v-if="advisorView && clientPortalStore.currentStep !== 'loading'"
      status="warning"
      class="stk-mb-4"
    >
      You are viewing this page as an <strong>advisor</strong>. You can navigate between pages, but cannot upload, edit, or delete any document.
    </Callout>

    <div
      v-if="clientPortalStore.currentStep === 'loading'"
      class="document-portal-loader"
    >
      <LoaderWithText inherit>
        {{ clientPortalStore.loadingMessage }}
      </LoaderWithText>
    </div>

    <DocumentPortalComplete
      v-else-if="clientPortalStore.currentStep === 'complete'"
    />

    <div
      v-else
      class="document-portal-window"
    >
      <DocumentPortalSidebar
        v-if="clientPortalStore.currentStep !== 'complete' && clientPortalStore.isLoggedIn"
      />

      <main class="document-portal-main">
        <!-- Welcome Screen -->
        <DocumentPortalWelcome
          v-if="clientPortalStore.currentStep === 'welcome'"
        />

        <!-- Document Upload Experience -->
        <DocumentPortalUpload
          v-else-if="clientPortalStore.currentStep === 'upload'"
        />

        <!-- Document Upload Experience -->
        <DocumentPortalOthers
          v-else-if="clientPortalStore.currentStep === 'others'"
        />
      </main>
    </div>

    <template v-if="advisorView">
      <!-- Advisor Management Drawer when in advisor view -->
      <ClientPortalDrawer
        v-model="clientPortalStore.showManagementDrawer"
        :entity-external-id="clientPortalStore.currentEntityId"
        :advisor-slug= "advisorSlug"
        :entity-name="clientPortalStore.clientName"
        :entity-email="clientPortalStore.clientInfo?.user?.email || clientPortalStore.clientInfo?.entity?.email  ||''"
        :portal-status="clientPortalStore.advisorInfo?.portal_status || clientPortalStore.clientInfo?.entity?.portal_status || 'not_invited'"
        :edit-entity="clientPortalStore.advisorInfo?.edit_entity || false "
        @invitation-link-generated="handleInvitationLinkGenerated"
      />

      <!-- Floating Management Button for Advisors -->
      <div
        class="floating-management-button"
      >
        <Button
          :icon-before="clientPortalStore.showManagementDrawer ? 'fas fa-times' : 'fas fa-cog'"
          variant="primary"
          class="management-toggle-btn"
          :title="clientPortalStore.showManagementDrawer ? 'Close Management Panel' : 'Open Management Panel'"
          @click="clientPortalStore.toggleManagementDrawer"
        >
          {{ clientPortalStore.showManagementDrawer ? 'Close' : 'Manage' }}
        </Button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';
import DocumentPortalUpload from '../components/DocumentPortalUpload.vue';
import DocumentPortalComplete from '../components/DocumentPortalComplete.vue';
import ClientPortalDrawer from '../components/ClientPortalDrawer.vue';
import DocumentPortalSidebar from '@frontend/ui/features/client-portal/components/DocumentPortalSidebar.vue';
import DocumentPortalWelcome from '@frontend/ui/features/client-portal/components/DocumentPortalWelcome.vue';
import DocumentPortalOthers from '@frontend/ui/features/client-portal/components/DocumentPortalOthers.vue';
import { Button, Callout, LoaderWithText } from '@stakeui';

// Define props
const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  firmSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: false,
    default: '',
  },
  portalType: {
    type: String,
    required: false,
    default: 'client', // 'firm', 'advisor', or 'client'
  },
  previewMode: {
    type: Boolean,
    default: false,
  },
  advisorView: {
    type: Boolean,
    default: false,
  },
});

const clientPortalStore = useClientPortalStore();

const handleInvitationLinkGenerated = data => {
  console.log('Invitation link generated:', data);
  // Could show a notification or update the UI
};

onMounted(async () => {
  await clientPortalStore.initializePortal({
    token: props.token,
    entityExternalId: props.entityExternalId,
    advisorSlug: props.advisorSlug,
    firmSlug: props.firmSlug,
    advisorView: props.advisorView,
  });
});

onBeforeUnmount(() => {
  clientPortalStore.stopPollingChecklist();
});
</script>

<style scoped>
.document-portal-view {
  background-color: var(--stk-text-90);
  min-height: 100vh;
  padding: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &.is-complete {
    background-color: var(--stk-text-95);
  }
}

.document-portal-loader {
  color: var(--stk-text-weak);
}

.document-portal-window {
  box-shadow: 0px 32px 32px 0px rgba(0, 0, 0, 0.02), 0px 16px 16px 0px rgba(0, 0, 0, 0.02), 0px 8px 8px 0px rgba(0, 0, 0, 0.02), 0px 4px 4px 0px rgba(0, 0, 0, 0.02), 0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.02);
  background-color: #fff;
  border-radius: 1.5em;
  /* max-width: 67.5em; */
  width: 100%;
  flex-grow: 1;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  max-width: 72em;
  height: calc(100vh - 6rem);
  min-height: 40rem;
}

.document-portal-main {
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  overflow: hidden;
}

.client-main {
  flex-grow: 1;
  padding: 2em;
}

/* Floating Management Button for Advisors */
.floating-management-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 999;
}

.management-toggle-btn {
  border-radius: 2rem;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.management-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

@media screen and (max-width: 60rem) {
  .document-portal-view {
    padding: 1rem;
  }

  .document-portal-window {
    flex-direction: column;
  }
}
</style>
